import { cloneDeep, mapValues } from 'lodash';

import { ContractExtractionCriterion } from '@evidentid/tprm-portal-lib/models/contract-extraction';
import {
    Criterion,
    CriterionField,
    CriterionInput,
    CriterionTemplate,
} from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

import {
    EditableRequirementCriterion,
} from '@/modules/decisioning-criteria/models/EditableRequirements.model';

export function riskProfileCriterionToEditableRequirementCriterion(
    criterion: Criterion | CriterionInput,
): EditableRequirementCriterion {
    return {
        id: 'id' in criterion ? criterion.id : undefined,
        coverageGroupId: 'coverageGroupId' in criterion ? (criterion as any).coverageGroupId : undefined,
        coverageType: criterion.coverageType,
        field: criterion.field,
        references: mapValues(criterion.evaluator.references, (ref) => cloneDeep(ref.value)),
        verify: criterion.verify,
        emailMessage: criterion.emailMessage,
        deprecated: criterion.deprecated,
    };
}

export function riskProfileCriteriaToEditableRequirementCriteria(
    criteria: (Criterion | CriterionInput)[],
): EditableRequirementCriterion[] {
    return criteria.map(riskProfileCriterionToEditableRequirementCriterion);
}

export function editableRequirementCriterionToRiskProfileCriterion(
    criterion: EditableRequirementCriterion,
    templateReferences: Record<string, CriterionField> = {},
): Criterion | CriterionInput {
    const baseCriterion = {
        coverageType: criterion.coverageType,
        field: criterion.field,
        evaluator: {
            references: mapValues(criterion.references, (value, key) => ({
                value: cloneDeep(value),
                schema: cloneDeep(templateReferences[key]?.schema ?? null),
            })),
        },
        verify: criterion.verify,
        emailMessage: criterion.emailMessage || '',
        deprecated: criterion.deprecated || false,
    };

    if (criterion.id) {
        return {
            ...baseCriterion,
            id: criterion.id,
        } as Criterion;
    }

    return baseCriterion as CriterionInput;
}

export function editableRequirementCriteriaToRiskProfileCriteria(
    criteria: EditableRequirementCriterion[],
    templateReferences: Record<string, CriterionField> = {},
): (Criterion | CriterionInput)[] {
    return criteria.map((criterion) =>
        editableRequirementCriterionToRiskProfileCriterion(criterion, templateReferences),
    );
}

export function contractExtractionCriterionToEditableRequirementCriterion(
    criterion: ContractExtractionCriterion,
    template?: CriterionTemplate | null,
): EditableRequirementCriterion {
    const templateReferences = template?.evaluator.references || {};
    const references = Object.keys({
        ...templateReferences,
        ...criterion.references,
    }).reduce((acc, key) => ({
        ...acc,
        [key]: cloneDeep(criterion.references[key] ?? templateReferences[key]?.value),
    }), {});

    return {
        id: criterion.id,
        coverageGroupId: criterion.coverageGroupId,
        coverageType: criterion.coverageType,
        field: criterion.field,
        references,
        verify: criterion.verify,
    };
}

export function editableRequirementCriterionToContractExtractionCriterion(
    criterion: EditableRequirementCriterion,
): ContractExtractionCriterion {
    return {
        id: criterion.id || '',
        coverageGroupId: criterion.coverageGroupId,
        coverageType: criterion.coverageType,
        field: criterion.field,
        references: mapValues(criterion.references, (value) => cloneDeep(value)),
        verify: criterion.verify,
    };
}
